import {
  STUDY_MIND_MAP_EDGE_ARROWS,
  STUDY_MIND_MAP_EDGE_COLORS,
  STUDY_MIND_MAP_EDGE_LINES,
  STUDY_MIND_MAP_EDGE_SHAPES,
  STUDY_MIND_MAP_EDGE_WIDTHS,
  type StudyMindMapContent,
  type StudyMindMapEdge,
  type StudyMindMapEdgeArrow,
  type StudyMindMapEdgeColor,
  type StudyMindMapEdgeLine,
  type StudyMindMapEdgeShape,
  type StudyMindMapEdgeWidth,
} from './studyMindMapApi';

/**
 * Link styles (study-mind-map-prd.md §4.3, owner feedback 2026-10-09, modelled on the JR roadmap
 * editor's edge panel): colour, line, arrow, shape and thickness. The map stores a style name,
 * never a hex value; each name maps to a DESIGN.md token class here. A default value is never
 * stored, so older links and links without a style stay exactly as they were.
 */

export interface MindMapEdgeStyle {
  color?: StudyMindMapEdgeColor;
  line?: StudyMindMapEdgeLine;
  arrow?: StudyMindMapEdgeArrow;
  shape?: StudyMindMapEdgeShape;
  width?: StudyMindMapEdgeWidth;
}

const STYLE_VALUES = {
  color: STUDY_MIND_MAP_EDGE_COLORS,
  line: STUDY_MIND_MAP_EDGE_LINES,
  arrow: STUDY_MIND_MAP_EDGE_ARROWS,
  shape: STUDY_MIND_MAP_EDGE_SHAPES,
  width: STUDY_MIND_MAP_EDGE_WIDTHS,
} as const;

export const MIND_MAP_EDGE_STYLE_KEYS = ['color', 'line', 'arrow', 'shape', 'width'] as const;
export type MindMapEdgeStyleKey = (typeof MIND_MAP_EDGE_STYLE_KEYS)[number];

/** The valid, non-default style fields of a link (what is saved). */
export function edgeStyleFields(edge: MindMapEdgeStyle): MindMapEdgeStyle {
  const fields: Record<string, string> = {};
  for (const key of MIND_MAP_EDGE_STYLE_KEYS) {
    const values: readonly string[] = STYLE_VALUES[key];
    const value = edge[key];
    if (typeof value === 'string' && values.includes(value) && value !== values[0])
      fields[key] = value;
  }
  return fields as MindMapEdgeStyle;
}

/** The full style of a link, with every default filled in. */
export function edgeStyle(edge: MindMapEdgeStyle): Required<MindMapEdgeStyle> {
  return {
    color: STUDY_MIND_MAP_EDGE_COLORS[0],
    line: STUDY_MIND_MAP_EDGE_LINES[0],
    arrow: STUDY_MIND_MAP_EDGE_ARROWS[0],
    shape: STUDY_MIND_MAP_EDGE_SHAPES[0],
    width: STUDY_MIND_MAP_EDGE_WIDTHS[0],
    ...edgeStyleFields(edge),
  };
}

/** Changes one or more style fields of a link. Unchanged content (same object) when nothing changes. */
export function setEdgeStyle(
  content: StudyMindMapContent,
  id: string,
  patch: MindMapEdgeStyle,
): StudyMindMapContent {
  const edge = content.edges.find((item) => item.id === id);
  if (!edge) return content;
  const before = edgeStyleFields(edge);
  const after = edgeStyleFields({ ...before, ...patch });
  const same = MIND_MAP_EDGE_STYLE_KEYS.every((key) => before[key] === after[key]);
  if (same) return content;
  const next: StudyMindMapEdge = { ...edge };
  for (const key of MIND_MAP_EDGE_STYLE_KEYS) delete next[key];
  Object.assign(next, after);
  return { ...content, edges: content.edges.map((item) => (item.id === id ? next : item)) };
}

/** Colour swatches: token classes for the line, the arrow head and the panel swatch. */
export const MIND_MAP_EDGE_COLOR_OPTIONS: ReadonlyArray<{
  value: StudyMindMapEdgeColor;
  label: string;
  stroke: string;
  marker: string;
  swatch: string;
  preview: string;
}> = [
  {
    value: 'neutral',
    label: 'Grey',
    stroke: '[&_[class*=edge-path]]:!stroke-stone2',
    marker: 'fill-stone2',
    swatch: 'bg-stone2',
    preview: 'stroke-stone2',
  },
  {
    value: 'ink',
    label: 'Black',
    stroke: '[&_[class*=edge-path]]:!stroke-ink',
    marker: 'fill-ink',
    swatch: 'bg-ink',
    preview: 'stroke-ink',
  },
  {
    value: 'slate',
    label: 'Slate',
    stroke: '[&_[class*=edge-path]]:!stroke-slate2',
    marker: 'fill-slate2',
    swatch: 'bg-slate2',
    preview: 'stroke-slate2',
  },
  {
    value: 'coral',
    label: 'Coral',
    stroke: '[&_[class*=edge-path]]:!stroke-brand-coral',
    marker: 'fill-brand-coral',
    swatch: 'bg-brand-coral',
    preview: 'stroke-brand-coral',
  },
  {
    value: 'bubblegum',
    label: 'Pink',
    stroke: '[&_[class*=edge-path]]:!stroke-brand-bubblegum',
    marker: 'fill-brand-bubblegum',
    swatch: 'bg-brand-bubblegum',
    preview: 'stroke-brand-bubblegum',
  },
  {
    value: 'sunshine',
    label: 'Yellow',
    stroke: '[&_[class*=edge-path]]:!stroke-brand-sunshine',
    marker: 'fill-brand-sunshine',
    swatch: 'bg-brand-sunshine',
    preview: 'stroke-brand-sunshine',
  },
  {
    value: 'sky',
    label: 'Blue',
    stroke: '[&_[class*=edge-path]]:!stroke-brand-sky',
    marker: 'fill-brand-sky',
    swatch: 'bg-brand-sky',
    preview: 'stroke-brand-sky',
  },
  {
    value: 'mint',
    label: 'Green',
    stroke: '[&_[class*=edge-path]]:!stroke-brand-mint',
    marker: 'fill-brand-mint',
    swatch: 'bg-brand-mint',
    preview: 'stroke-brand-mint',
  },
];

export const MIND_MAP_EDGE_LINE_OPTIONS: ReadonlyArray<{
  value: StudyMindMapEdgeLine;
  label: string;
}> = [
  { value: 'solid', label: 'Solid' },
  { value: 'dashed', label: 'Dashed' },
  { value: 'dotted', label: 'Dotted' },
];

export const MIND_MAP_EDGE_ARROW_OPTIONS: ReadonlyArray<{
  value: StudyMindMapEdgeArrow;
  label: string;
}> = [
  { value: 'none', label: 'No arrow' },
  { value: 'end', label: 'Arrow at end' },
  { value: 'start', label: 'Arrow at start' },
  { value: 'both', label: 'Arrows both ends' },
];

/** Shapes map to React Flow's built-in edge types (JR roadmap EDGE_TYPES). */
export const MIND_MAP_EDGE_SHAPE_OPTIONS: ReadonlyArray<{
  value: StudyMindMapEdgeShape;
  label: string;
  flowType: 'default' | 'smoothstep' | 'straight';
}> = [
  { value: 'curve', label: 'Curve', flowType: 'default' },
  { value: 'elbow', label: 'Elbow', flowType: 'smoothstep' },
  { value: 'straight', label: 'Straight', flowType: 'straight' },
];

export const MIND_MAP_EDGE_WIDTH_OPTIONS: ReadonlyArray<{
  value: StudyMindMapEdgeWidth;
  label: string;
  px: number;
}> = [
  { value: 'thin', label: 'Thin', px: 1.5 },
  { value: 'normal', label: 'Normal', px: 2 },
  { value: 'thick', label: 'Thick', px: 4 },
];

/** A selected link is drawn this much thicker. */
const SELECTED_EXTRA_PX = 1.5;

function colorOption(value: StudyMindMapEdgeColor) {
  return (
    MIND_MAP_EDGE_COLOR_OPTIONS.find((option) => option.value === value) ??
    MIND_MAP_EDGE_COLOR_OPTIONS[0]
  );
}

/** Id of the arrow head marker of a colour (`StudyMindMapArrowMarkers` draws one per colour). */
export function mindMapArrowMarkerId(color: StudyMindMapEdgeColor): string {
  return `study-mind-map-arrow-${color}`;
}

/** A dash and the gap after it, in stroke widths. */
const DASH_ON = 4;
const DASH_OFF = 3;

/** SVG dash pattern of a line style at a stroke width; undefined for a solid line. */
export function edgeDashArray(line: StudyMindMapEdgeLine, px: number): string | undefined {
  if (line === 'dashed') return `${px * DASH_ON} ${px * DASH_OFF}`;
  if (line === 'dotted') return `0 ${px * 2.5}`;
  return undefined;
}

/** Speed of the dashes on a dashed link, in canvas pixels per second (owner request 2026-10-09: slow). */
export const MIND_MAP_DASH_FLOW_PX_PER_SECOND = 10;
/** Moves the dashes of a dashed link; off when the device asks for reduced motion. */
const DASH_FLOW_CLASS = 'motion-safe:[&_[class*=edge-path]]:animate-dash-flow';

export interface FlowEdgeLook {
  type: 'default' | 'smoothstep' | 'straight';
  className: string;
  style: {
    strokeWidth: number;
    strokeDasharray?: string;
    strokeLinecap?: 'round';
    /** Dashed links only: one dash plus one gap, the distance of one animation loop. */
    '--dash-period'?: string;
    animationDuration?: string;
  };
  markerStart?: string;
  markerEnd?: string;
}

/** How React Flow draws a link: edge type, token colour class, width, dashes and arrow heads. */
export function flowEdgeLook(edge: MindMapEdgeStyle, selected: boolean): FlowEdgeLook {
  const style = edgeStyle(edge);
  const width =
    (MIND_MAP_EDGE_WIDTH_OPTIONS.find((option) => option.value === style.width)?.px ?? 2) +
    (selected ? SELECTED_EXTRA_PX : 0);
  const dash = edgeDashArray(style.line, width);
  const marker = mindMapArrowMarkerId(style.color);
  const flowType =
    MIND_MAP_EDGE_SHAPE_OPTIONS.find((option) => option.value === style.shape)?.flowType ??
    'default';
  const flows = style.line === 'dashed';
  const period = width * (DASH_ON + DASH_OFF);
  return {
    type: flowType,
    className: `${colorOption(style.color).stroke}${flows ? ` ${DASH_FLOW_CLASS}` : ''}${selected ? ' study-mind-map-edge-selected' : ''}`,
    style: {
      strokeWidth: width,
      ...(dash ? { strokeDasharray: dash } : {}),
      ...(flows
        ? {
            '--dash-period': `${period}px`,
            animationDuration: `${period / MIND_MAP_DASH_FLOW_PX_PER_SECOND}s`,
          }
        : {}),
      ...(style.line === 'dotted' ? { strokeLinecap: 'round' as const } : {}),
    },
    ...(style.arrow === 'start' || style.arrow === 'both' ? { markerStart: marker } : {}),
    ...(style.arrow === 'end' || style.arrow === 'both' ? { markerEnd: marker } : {}),
  };
}

/** Token stroke class and dash pattern of a link on the small preview picture. */
export function previewEdgeLook(edge: {
  color?: StudyMindMapEdgeColor;
  line?: StudyMindMapEdgeLine;
}) {
  const style = edgeStyle(edge);
  return { stroke: colorOption(style.color).preview, dash: edgeDashArray(style.line, 1.5) };
}
